import React from 'react';
import { z } from 'zod';
import { Control, useFieldArray, useFormState } from 'react-hook-form';
import { AddCircleOutlined, InfoOutlined, RemoveCircleOutlined } from '@material-ui/icons';
import IconButton from '@material-ui/core/IconButton';
import FormHelperText from '@material-ui/core/FormHelperText';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import FormTextField from './FormTextField';

export type ExtraRole = {
  role: string;
  name: string;
  company: string;
  email: string;
};

const EXTRA_ROLE_FIELDS = ['role', 'name', 'company', 'email'] as const;

export const INCOMPLETE_EXTRA_ROLE_MESSAGE = 'Complete every field in this row, or clear it.';

export const EXTRA_ROLES_HINT =
  'If you fill in any field for a person, fill in all four — partially completed rows cannot be saved.';

const hasValue = (value?: string) => !!value?.trim();

/**
 * A row only counts as started once it names a person. Company is pre-filled for some groups, so
 * on its own it does not make a row required — blank rows are dropped when the form is saved.
 */
export const isExtraRoleRowStarted = (row: Partial<ExtraRole>) =>
  hasValue(row.role) || hasValue(row.name) || hasValue(row.email);

/**
 * The API rejects an extra role that is missing any of its fields, so a started row must be
 * completed before the form can be saved. Every empty field in that row is flagged individually.
 */
export const extraRolesSchema = z
  .array(
    z.object({
      role: z.string(),
      name: z.string(),
      company: z.string(),
      email: z.string(),
    }),
  )
  .optional()
  .superRefine((rows, ctx) => {
    rows?.forEach((row, index) => {
      if (!isExtraRoleRowStarted(row)) return;

      EXTRA_ROLE_FIELDS.filter((field) => !hasValue(row[field])).forEach((field) =>
        ctx.addIssue({
          code: 'custom',
          path: [index, field],
          message: INCOMPLETE_EXTRA_ROLE_MESSAGE,
        }),
      );
    });
  });

const useStyles = makeStyles(() =>
  createStyles({
    hint: {
      display: 'inline-flex',
      alignItems: 'center',
      fontSize: 12,
      lineHeight: '18px',
      color: '#616061',
      textTransform: 'none',
      '@media print': {
        display: 'none',
      },
    },
    hintIcon: {
      fontSize: 16,
      marginRight: 4,
    },
  }),
);

interface ProjectExtraRoleFieldsProps {
  control: Control<any>;
  /** Name of the field array holding the rows, e.g. `graceOtherRoles`. */
  name: string;
  canEdit: boolean;
  isEditing: boolean;
  /** Pre-filled company for rows added to this group. */
  defaultCompany?: string;
}

/**
 * The user-defined role rows of a group in the Project Request form. Renders pairs of grid cells,
 * so it has to stay a direct child of the form's grid.
 */
export function ProjectExtraRoleFields(props: ProjectExtraRoleFieldsProps) {
  const { control, name, canEdit, isEditing, defaultCompany = '' } = props;
  const classes = useStyles();

  const { fields, append, remove } = useFieldArray({ control, name });
  const { errors } = useFormState({ control, name });

  return (
    <>
      {fields.map((field, index) => (
        <React.Fragment key={field.id}>
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              alignSelf: 'flex-start',
              gap: 10,
            }}
          >
            {canEdit ? (
              <IconButton
                onClick={() => remove(index)}
                disabled={!isEditing}
                style={{ width: 'min-content', padding: 0 }}
              >
                <RemoveCircleOutlined />
              </IconButton>
            ) : null}
            <FormTextField
              name={`${name}.${index}.role`}
              placeholder="Role"
              control={control}
              style={{ width: '60%' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}
            >
              <FormTextField
                name={`${name}.${index}.name`}
                placeholder="Name"
                control={control}
                style={{ width: '100%' }}
              />
              <FormTextField
                name={`${name}.${index}.company`}
                placeholder="Company"
                control={control}
                style={{ width: '100%' }}
              />
              <FormTextField
                name={`${name}.${index}.email`}
                placeholder="Email"
                control={control}
                style={{ width: '100%' }}
              />
            </div>
            {_.get(errors, `${name}.${index}`) ? (
              <FormHelperText error>{INCOMPLETE_EXTRA_ROLE_MESSAGE}</FormHelperText>
            ) : null}
          </div>
        </React.Fragment>
      ))}
      {canEdit ? (
        <>
          <div>
            <IconButton
              onClick={() => append({ role: '', name: '', company: defaultCompany, email: '' })}
              disabled={!isEditing}
              style={{ width: 'min-content', padding: 0 }}
            >
              <AddCircleOutlined />
            </IconButton>
          </div>
          <Typography variant="caption" className={classes.hint}>
            <InfoOutlined className={classes.hintIcon} />
            {EXTRA_ROLES_HINT}
          </Typography>
        </>
      ) : null}
    </>
  );
}
